import clsx from 'clsx';

import RichText from '@commons/components/RichText';
import Texture from '@commons/components/Texture';
import { Grid, type GridSize } from '@commons/containers/Grid';
import { generateId } from '@commons/helpers/string';
import type { DrupalBlock } from '@commons/types/drupal';
import type { Alignment, ResponsiveStyle, Spacing, Theme } from '@commons/types/styles';

import styles from './index.module.scss';

// Type definition for the NestedSection block component properties.

type Props = DrupalBlock<{
  items: string[];
  columns: GridSize;
  gap: Spacing;
  padding: Spacing;
  verticalAlignment: Alignment;
  roundCorner: boolean;
  theme: Theme;
  texture: boolean;
  animated?: boolean;
}>;

/**
 * NestedSection block component.
 */

const NestedSectionBlock = ({ uuid, content }: Props) => {
  const { items, columns, gap, padding, verticalAlignment, roundCorner, theme, texture, animated } =
    content;

  const isColumnsEven = columns % 2 === 0;
  const responsiveColumns: ResponsiveStyle<GridSize> = {
    xs: 1,
    sm: 1,
    md: Math.min(columns, 2) as GridSize,
    lg: Math.min(columns, isColumnsEven ? 2 : 3) as GridSize,
    xl: columns,
  };

  const renderedItems = items.map((item, index) => {
    const id = generateId(`${uuid}-${index}`);
    return <RichText value={item} className={styles.item} key={id} />;
  });

  const classes = clsx(styles.nestedSection, roundCorner && styles.roundCorner);

  return (
    <Grid
      width={12}
      gap={gap}
      theme={theme}
      align={verticalAlignment || 'center'}
      padding={padding}
      columns={responsiveColumns}
      className={classes}
    >
      {renderedItems}
      {texture && <Texture animated={animated} />}
    </Grid>
  );
};

export default NestedSectionBlock;
